import {
  Accordion,
  Checkbox,
  CustomRadioGroup,
  FormInput,
  SkeletonLoader,
} from '@ui-kit'
import React, { useEffect, useState } from 'react'
import { STRINGS } from '../../../utils/strings'
import { useFormContext, useWatch } from 'react-hook-form'
import {
  ENTITY_FIELDS,
  ENTITY_FIELDS_META,
  ENTITY_NAME,
  HYBRID_LAYOUT_CONSTANTS,
} from '../../../utils/constants'
import {
  getGenericEntityListingAPI,
  GetGenericEntityListingApiType,
} from '@app-store'
import {
  mergeNestedObjects,
  normalizeLookups,
} from '../../../utils/hybridFormRendererfuntions.utils'

const keys = {
  patient_prescription_coverage: `${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${ENTITY_NAME.CASE_PAP_DETAILS}.${ENTITY_FIELDS.CASE_PAP_DETAILS.patient_prescription_coverage}`,
  employer_supplied_or_private_drug_coverage: `${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${ENTITY_NAME.CASE_PAP_DETAILS}.${ENTITY_FIELDS.CASE_PAP_DETAILS.employer_supplied_or_private_drug_coverage}`,
  va_or_military_benefits: `${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${ENTITY_NAME.CASE_PAP_DETAILS}.${ENTITY_FIELDS.CASE_PAP_DETAILS.va_or_military_benefits}`,
  medicare_advantage: `${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${ENTITY_NAME.CASE_PAP_DETAILS}.${ENTITY_FIELDS.CASE_PAP_DETAILS.medicare_advantage}`,
  medicare_part_d: `${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${ENTITY_NAME.CASE_PAP_DETAILS}.${ENTITY_FIELDS.CASE_PAP_DETAILS.medicare_part_d}`,
  medicare_part_b: `${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${ENTITY_NAME.CASE_PAP_DETAILS}.${ENTITY_FIELDS.CASE_PAP_DETAILS.medicare_part_b}`,
  medicaid_prescription_drug_coverage: `${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${ENTITY_NAME.CASE_PAP_DETAILS}.${ENTITY_FIELDS.CASE_PAP_DETAILS.medicaid_prescription_drug_coverage}`,
  lis_extra_help: `${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${ENTITY_NAME.CASE_PAP_DETAILS}.${ENTITY_FIELDS.CASE_PAP_DETAILS.lis_extra_help}`,
  part_d_plan: `${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${ENTITY_NAME.CASE_PAP_DETAILS}.${ENTITY_FIELDS.CASE_PAP_DETAILS.part_d_plan}`,
}

const NovoInsuranceInformationComponent = ({
  dataId,
  entityId,
  mode,
}: {
  dataId: string
  entityId: string
  mode: string | undefined
}) => {
  const { register, setValue, getValues } = useFormContext()
  const [loading, setLoading] = useState<boolean>(false)

  const isCoverageSelected = useWatch({
    name: keys.patient_prescription_coverage,
  })

  const showYes = isCoverageSelected === 'Yes'
  const showNo = isCoverageSelected === 'No'

  const isPartDSelected = useWatch({ name: keys.medicare_part_d })

  const fetchData = async () => {
    if (!dataId) {
      setValue(keys.patient_prescription_coverage, 'Yes')
      return
    }
    setLoading(true)
    const payload: GetGenericEntityListingApiType = {
      entity_id: entityId,
      select: [ENTITY_NAME.CASE_PAP_DETAILS],
      page: 0,
      size: 25,
      related_entities: {
        [ENTITY_NAME.CASE_PAP_DETAILS]: {
          select: [
            ENTITY_FIELDS.CASE_PAP_DETAILS.patient_prescription_coverage,
            ENTITY_FIELDS.CASE_PAP_DETAILS.va_or_military_benefits,
            ENTITY_FIELDS.CASE_PAP_DETAILS.medicare_advantage,
            ENTITY_FIELDS.CASE_PAP_DETAILS.part_d_plan,
            ENTITY_FIELDS.CASE_PAP_DETAILS.medicare_part_b,
            ENTITY_FIELDS.CASE_PAP_DETAILS.medicare_part_d,
            ENTITY_FIELDS.CASE_PAP_DETAILS.lis_extra_help,
            ENTITY_FIELDS.CASE_PAP_DETAILS.part_d_plan,
            ENTITY_FIELDS.CASE_PAP_DETAILS
              .employer_supplied_or_private_drug_coverage,
            ENTITY_FIELDS.CASE_PAP_DETAILS.medicaid_prescription_drug_coverage,
          ],
          criteria: [
            {
              criteria: [],
            },
          ],
        },
      },
      filters: [
        {
          criteria: [],
        },
      ],
    }
    try {
      const response = await getGenericEntityListingAPI(payload, dataId)

      if (response) {
        const normalized = normalizeLookups(response)
        const val = getValues()
        const endData = mergeNestedObjects(val, normalized)
        setValue(
          `${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${ENTITY_NAME.CASE_PAP_DETAILS}`,
          {
            ...val[
              `${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${ENTITY_NAME.CASE_PAP_DETAILS}`
            ],
            ...endData?.[HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY]?.[
              ENTITY_NAME.CASE_PAP_DETAILS
            ],
          }
        )
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isPartDSelected !== null && !isPartDSelected) {
      setValue(keys.part_d_plan, '')
    }
  }, [isPartDSelected])

  useEffect(() => {
    if (isCoverageSelected === 'No') {
      setValue(keys.employer_supplied_or_private_drug_coverage, false)
      setValue(keys.medicaid_prescription_drug_coverage, false)
      setValue(keys.va_or_military_benefits, false)
      setValue(keys.medicare_part_b, false)
      setValue(keys.medicare_advantage, false)
      setValue(keys.lis_extra_help, false)
      setValue(keys.medicare_part_d, false)
      setValue(keys.part_d_plan, '')
    }
  }, [isCoverageSelected])

  useEffect(() => {
    fetchData()
  }, [])

  if (loading) {
    return <SkeletonLoader rows={4} columns={4} />
  }

  return (
    <div>
      <Accordion
        heading={
          <div>
            <span>{STRINGS.INSURANCE_INFORMATION}</span>
          </div>
        }
        isDefaultActive={true}
        isConditional
      >
        <div className="p-3 h-[18rem] text-sm">
          <div className="flex items-center gap-4">
            <span>{STRINGS.PATIENT_PRESCRIPTION_COVERAGE}</span>
            {mode === 'view' ? (
              <span className="text-xs font-extraLight">
                {getValues(keys.patient_prescription_coverage) ?? '--'}
              </span>
            ) : (
              <CustomRadioGroup
                options={
                  ENTITY_FIELDS_META.CASE_PAP_DETAILS
                    .patient_prescription_coverage.options
                }
                name={keys.patient_prescription_coverage}
                register={register}
              />
            )}
          </div>
          {mode === 'view' ? (
            <div className="mt-3">{STRINGS.INSURANCE_TYPE}</div>
          ) : null}
          {showNo && (
            <div className="mt-2">
              {mode === 'view' ? (
                <span className="text-xs font-extraLight">
                  {STRINGS.UNINSURED}
                </span>
              ) : (
                <FormInput
                  name="__EXTRA__"
                  label={STRINGS.INSURANCE_TYPE}
                  value="Uninsured"
                  disabled
                />
              )}
            </div>
          )}
          {showYes && (
            <div
              className={`${
                mode === 'view'
                  ? 'flex flex-col gap-2'
                  : 'grid grid-cols-2 gap-3 mt-4'
              }`}
            >
              {mode === 'view' ? (
                getValues(keys.employer_supplied_or_private_drug_coverage) && (
                  <span className="text-xs font-extraLight">
                    {STRINGS.EMPLOYER_SUPPLIED_OR_PRIVATE_DRUG_COVERAGE}
                  </span>
                )
              ) : (
                <Checkbox
                  name={keys.employer_supplied_or_private_drug_coverage}
                  label={STRINGS.EMPLOYER_SUPPLIED_OR_PRIVATE_DRUG_COVERAGE}
                  register={register}
                />
              )}
              {mode === 'view' ? (
                getValues(keys.medicaid_prescription_drug_coverage) && (
                  <span className="text-xs font-extraLight">
                    {STRINGS.MEDICAID_PRESCRIPTION_DRUG_COVERAGE}
                  </span>
                )
              ) : (
                <Checkbox
                  name={keys.medicaid_prescription_drug_coverage}
                  label={STRINGS.MEDICAID_PRESCRIPTION_DRUG_COVERAGE}
                  register={register}
                />
              )}
              {mode === 'view' ? (
                getValues(keys.va_or_military_benefits) && (
                  <span className="text-xs font-extraLight">
                    {STRINGS.VA_OR_MILITARY_BENEFITS}
                  </span>
                )
              ) : (
                <Checkbox
                  name={keys.va_or_military_benefits}
                  label={STRINGS.VA_OR_MILITARY_BENEFITS}
                  register={register}
                />
              )}
              {mode === 'view' ? (
                getValues(keys.medicare_part_b) && (
                  <span className="text-xs font-extraLight">
                    {STRINGS.MEDICARE_PART_B_MEDICAL_COVERAGE}
                  </span>
                )
              ) : (
                <Checkbox
                  name={keys.medicare_part_b}
                  label={STRINGS.MEDICARE_PART_B_MEDICAL_COVERAGE}
                  register={register}
                />
              )}
              {mode === 'view' ? (
                getValues(keys.medicare_advantage) && (
                  <span className="text-xs font-extraLight">
                    {STRINGS.MEDICARE_ADVANTAGE_PART_C}
                  </span>
                )
              ) : (
                <Checkbox
                  name={keys.medicare_advantage}
                  label={STRINGS.MEDICARE_ADVANTAGE_PART_C}
                  register={register}
                />
              )}
              {mode === 'view' ? (
                getValues(keys.lis_extra_help) && (
                  <span className="text-xs font-extraLight">
                    {STRINGS.MEDICARE_LOW_INCOME_SUBSIDY_LIS_EXTRA_HELP}
                  </span>
                )
              ) : (
                <Checkbox
                  name={keys.lis_extra_help}
                  label={STRINGS.MEDICARE_LOW_INCOME_SUBSIDY_LIS_EXTRA_HELP}
                  register={register}
                />
              )}
              <div>
                {mode === 'view' ? (
                  getValues(keys.medicare_part_d) && (
                    <span className="text-xs font-extraLight">
                      {
                        STRINGS.MEDICARE_PART_D_PRESCRIPTION_COVERAGE_PART_D_PLAN
                      }
                    </span>
                  )
                ) : (
                  <Checkbox
                    name={keys.medicare_part_d}
                    label={
                      STRINGS.MEDICARE_PART_D_PRESCRIPTION_COVERAGE_PART_D_PLAN
                    }
                    register={register}
                  />
                )}

                <div className="mt-4">
                  {mode === 'view' ? (
                    <div className="flex gap-2">
                      <span className="">{STRINGS.PART_D_PLAN_NAME} :</span>
                      <span className="text-xs font-extraLight">
                        {getValues(keys.part_d_plan) ?? '--'}
                      </span>
                    </div>
                  ) : (
                    <div className="mt-4">
                      <FormInput
                        register={register}
                        name={keys.part_d_plan}
                        label={STRINGS.PART_D_PLAN_NAME}
                        disabled={!isPartDSelected}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </Accordion>
    </div>
  )
}

export default NovoInsuranceInformationComponent
